import React, { useEffect, useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight, ArrowUpRight, Star, ExternalLink, Phone, MapPin, Clock, Wrench,
  DollarSign, ShieldCheck, UserCircle, Plus, Minus, ChevronDown, Sparkles,
  BadgeCheck, Navigation, Package, Gift
} from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import TickerBanner from '../components/TickerBanner';
import RecentlyViewedStrip from '../components/refurbished/RecentlyViewedStrip';
import { listingUrl } from '../lib/listingUrl';
import SEO, { buildLocalBusinessSchema } from '../components/SEO';
import { HomeContactChoice } from '../components/HomeContactChoice';

const API = process.env.REACT_APP_BACKEND_URL;

// Realistic hardware renders already shipped in /public/images/devices
const IMG = {
  storefront: '/images/store/storefront.jpg',
  interior: '/images/store/interior.jpg',
  fanBuyback: '/images/devices/fan_buyback.webp',
  repairBench: '/images/devices/repair_bench_pro.jpg',
  chicago: 'https://images.unsplash.com/photo-1668963012082-e4b5b860e3e0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600',
};

const GRADE_BG = { A: 'bg-emerald-400 text-emerald-950', B: 'bg-amber-400 text-amber-950', C: 'bg-orange-400 text-orange-950' };

const HomeNew = () => {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [shopDevices, setShopDevices] = useState([]);
  const [activeQuestion, setActiveQuestion] = useState(null);
  const [faqExpanded, setFaqExpanded] = useState(false);
  const [now, setNow] = useState(() => new Date());
  const rootRef = useRef(null);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  // The site-wide bootstrap script tags <html> as "dark" on the homepage.
  // This redesign uses a warm-pearl light base, so force light while mounted.
  useEffect(() => {
    const el = document.documentElement;
    el.classList.add('light'); el.classList.remove('dark');
    return () => {};
  }, []);

  // Live Chicago open/closed status (used only for the warm location card)
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);
  const chi = new Date(now.toLocaleString('en-US', { timeZone: 'America/Chicago' }));
  const ccDay = chi.getDay();
  const ccHour = chi.getHours();
  // Real store hours: Mon-Fri 10-18, Sat 10-16, Sun closed
  const HOURS = { 0: null, 1: [10, 18], 2: [10, 18], 3: [10, 18], 4: [10, 18], 5: [10, 18], 6: [10, 16] };
  const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const fmtHour = (h) => `${h % 12 || 12}${h >= 12 ? 'PM' : 'AM'}`;
  const todayHours = HOURS[ccDay];
  const isOpen = !!todayHours && ccHour >= todayHours[0] && ccHour < todayHours[1];
  let nextOpenLabel = '';
  if (todayHours && ccHour < todayHours[0]) {
    nextOpenLabel = `today ${fmtHour(todayHours[0])}`;
  } else {
    for (let i = 1; i <= 7; i++) {
      const d = (ccDay + i) % 7;
      if (HOURS[d]) { nextOpenLabel = `${DAY_NAMES[d]} ${fmtHour(HOURS[d][0])}`; break; }
    }
  }
  const storeStatus = isOpen ? `Open now · til ${fmtHour(todayHours[1])}` : `Closed now · opens ${nextOpenLabel}`;

  // Scroll reveal (CSS only, performant, respects reduced-motion)
  useEffect(() => {
    const els = rootRef.current?.querySelectorAll('[data-reveal]') || [];
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('in'); obs.unobserve(e.target); } });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  // Reviews (live) + refurbished showcase (live)
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/reviews/public?limit=20`);
        if (r.ok) { const d = await r.json(); if (d.reviews?.length) setReviews(d.reviews); }
      } catch { /* optional */ }
    })();
    (async () => {
      try {
        const r = await fetch(`${API}/api/public/marketplace/devices?limit=20&sort_by=newest`);
        if (r.ok) { const d = await r.json(); if (d.devices?.length) setShopDevices(d.devices.slice(0, 8)); }
      } catch { /* optional */ }
    })();
  }, []);

  const fallbackReviews = [
    { author_name: 'Simon N.', text: "Brought my broken old phone for a repair and the guy fixed everything. Screen, buttons, battery, antenna and back cover. Highly recommend!", rating: 5, device: 'Phone Repair', platform: 'yelp' },
    { author_name: 'Linda F.', text: 'Fast. Accurate. I did not need to wait. Patiently explained and answered all questions at a very reasonable price.', rating: 5, device: 'Computer Repair', platform: 'yelp' },
    { author_name: 'Annmarie C.', text: 'Quoted a price and time substantially lower than other places. They delivered exactly what was quoted.', rating: 5, device: 'Phone Repair', platform: 'yelp' },
    { author_name: 'Michael T.', text: 'Brought my iPhone 15 Pro with a cracked screen. Replaced in 25 minutes while I waited. Way less than the Apple Store.', rating: 5, device: 'iPhone 15 Pro', platform: 'google' },
    { author_name: 'Max P.', text: 'Amazing experience! Fast, friendly, convenient. Best prices compared to all the other shops I found.', rating: 5, device: 'Phone Repair', platform: 'yelp' },
  ];
  const displayReviews = reviews.length ? reviews : fallbackReviews;

  const faqs = [
    { q: 'How long does a repair take?', a: 'Most repairs are same-day. Screen repairs take 30-60 minutes.' },
    { q: 'Do you provide warranty?', a: 'Yes! 1-month warranty on all repairs, parts & labor.' },
    { q: 'What devices do you repair?', a: 'iPhones, Samsung, MacBooks, PS5, Xbox, Switch & more.' },
    { q: 'How do I sell my device?', a: 'Get an instant quote, ship free, get paid in 24 hours.' },
    { q: 'Where can I get instant cash for my phone in Chicago?', a: 'Get instant cash at 2A Electronics, 4859 W Irving Park Rd, Chicago, IL 60641. Competitive on-the-spot payouts for iPhones, iPads, MacBooks, and consoles in good working condition.' },
    { q: 'Do you buy broken or financed iPhones?', a: 'Yes. We buy brand new, used, finance-locked, and cracked or broken devices. Free 5-minute appraisal and immediate cash payout.' },
    { q: 'How long does an electronics repair take?', a: 'Most screen and battery replacements are same-day within 30 to 45 minutes. Console HDMI and laptop service typically take 1-2 business days.' },
  ];

  const scrollTo = (id) => { const el = document.getElementById(id); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); };

  return (
    <div ref={rootRef} className="tw-home" data-testid="home-2a-redesign">
      <SEO
        title="Electronics Repair Chicago | Phones, Computers & Consoles | 2A Electronics"
        description="Chicago's top-rated electronics repair shop. We fix phones, computers, tablets & game consoles, and pay cash for used devices. Same-day service, free diagnostics, 1-month warranty."
        keywords="electronics repair chicago, phone repair chicago, computer repair chicago, iphone repair chicago, sell my phone chicago, cash for phones chicago, electronics buyback chicago, phone repair near me"
        canonical="/"
        speakable={true}
        schemas={[buildLocalBusinessSchema({ city: 'Chicago', state: 'Illinois', stateAbbr: 'IL', ratingValue: '5.0', reviewCount: '470' })]}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "2A Electronics Service",
          "url": "https://2aelectronics.com",
          "potentialAction": {"@type": "SearchAction", "target": "https://2aelectronics.com/repairs?q={search_term_string}", "query-input": "required name=search_term_string"}
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ElectronicsRepairShop",
          "@id": "https://2aelectronics.com/#localbusiness",
          "name": "2A Electronics Service",
          "image": "https://2aelectronics.com/logo.png",
          "logo": "https://2aelectronics.com/logo.png",
          "url": "https://2aelectronics.com",
          "description": "Professional cell phone, tablet, computer, and electronics repair plus same-day device buyback in Chicago. Free diagnostics, 1-year repair protection plan.",
          "telephone": "+1-773-766-5814",
          "priceRange": "$$",
          "address": {"@type": "PostalAddress", "streetAddress": "4859 W Irving Park Rd", "addressLocality": "Chicago", "addressRegion": "IL", "postalCode": "60641", "addressCountry": "US"},
          "geo": {"@type": "GeoCoordinates", "latitude": 41.9535035, "longitude": -87.7497223},
          "hasMap": "https://www.google.com/maps/search/?api=1&query=2A+Electronics+Service&query_place_id=ChIJw442ZgPJD4gRk8V6dYznlw8",
          "openingHoursSpecification": [
            {"@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"], "opens": "10:00", "closes": "18:00"},
            {"@type": "OpeningHoursSpecification", "dayOfWeek": "Saturday", "opens": "10:00", "closes": "16:00"}
          ],
          "aggregateRating": {"@type": "AggregateRating", "ratingValue": "5.0", "reviewCount": "389", "bestRating": "5", "worstRating": "1"},
          "makesOffer": [
            {"@type": "Offer", "name": "Cash for Used & Broken Electronics", "description": "We buy used and broken iPhones, Samsung phones, iPads, MacBooks, laptops and game consoles for cash at our Chicago storefront.", "priceCurrency": "USD", "businessFunction": "http://purl.org/goodrelations/v1#Buy", "itemOffered": {"@type": "Product", "name": "Used & broken phones, tablets, laptops, and game consoles", "category": "Consumer electronics buyback"}, "areaServed": {"@type": "City", "name": "Chicago"}, "availableAtOrFrom": {"@type": "Place", "name": "2A Electronics Service", "address": {"@type": "PostalAddress", "streetAddress": "4859 W Irving Park Rd", "addressLocality": "Chicago", "addressRegion": "IL", "postalCode": "60641", "addressCountry": "US"}}}
          ],
          "sameAs": [
            "https://www.google.com/maps/search/?api=1&query=2A+Electronics+Service&query_place_id=ChIJw442ZgPJD4gRk8V6dYznlw8",
            "https://www.facebook.com/2AElectronicsService/",
            "https://www.instagram.com/2aelectronicsservice/",
            "https://www.yelp.com/biz/2a-electronics-service-chicago"
          ]
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "2A Electronics Service",
          "url": "https://2aelectronics.com",
          "logo": "https://2aelectronics.com/logo.png",
          "contactPoint": [
            {"@type": "ContactPoint", "telephone": "+1-773-766-5814", "contactType": "customer service", "areaServed": "US", "availableLanguage": ["English"], "contactOption": "TollFree", "hoursAvailable": [
              {"@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"], "opens": "10:00", "closes": "18:00"},
              {"@type": "OpeningHoursSpecification", "dayOfWeek": "Saturday", "opens": "10:00", "closes": "16:00"}
            ]},
            {"@type": "ContactPoint", "email": "2aelectronicsservice@gmail.com", "contactType": "customer support", "areaServed": "US"}
          ]
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            { "@type": "Service", "name": "iPhone Repair Chicago", "provider": {"@id": "https://2aelectronics.com/#business"}, "areaServed": "Chicago, IL", "aggregateRating": {"@type": "AggregateRating", "ratingValue": "4.9", "reviewCount": "215", "bestRating": "5"}},
            { "@type": "Service", "name": "Samsung Galaxy Repair Chicago", "provider": {"@id": "https://2aelectronics.com/#business"}, "areaServed": "Chicago, IL", "aggregateRating": {"@type": "AggregateRating", "ratingValue": "4.9", "reviewCount": "98", "bestRating": "5"}},
            { "@type": "Service", "name": "MacBook Repair Chicago", "provider": {"@id": "https://2aelectronics.com/#business"}, "areaServed": "Chicago, IL", "aggregateRating": {"@type": "AggregateRating", "ratingValue": "4.9", "reviewCount": "76", "bestRating": "5"}},
            { "@type": "Service", "name": "iPad Repair Chicago", "provider": {"@id": "https://2aelectronics.com/#business"}, "areaServed": "Chicago, IL", "aggregateRating": {"@type": "AggregateRating", "ratingValue": "4.9", "reviewCount": "54", "bestRating": "5"}},
            { "@type": "Service", "name": "Console Repair Chicago", "provider": {"@id": "https://2aelectronics.com/#business"}, "areaServed": "Chicago, IL", "aggregateRating": {"@type": "AggregateRating", "ratingValue": "4.9", "reviewCount": "47", "bestRating": "5"}}
          ]
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {"@type": "Question", "name": "How long does a repair take?", "acceptedAnswer": {"@type": "Answer", "text": "Most repairs are completed same-day. Screen repairs take 30-60 minutes."}},
            {"@type": "Question", "name": "Do you provide warranty?", "acceptedAnswer": {"@type": "Answer", "text": "Yes! 1-month warranty on all repairs, parts and labor."}},
            {"@type": "Question", "name": "What devices do you repair?", "acceptedAnswer": {"@type": "Answer", "text": "iPhones, Samsung, MacBooks, PS5, Xbox, Switch and more."}},
            {"@type": "Question", "name": "How do I sell my device?", "acceptedAnswer": {"@type": "Answer", "text": "Get instant quote, ship free, get paid in 24 hours."}},
            {"@type": "Question", "name": "Where can I get instant cash for my phone in Chicago?", "acceptedAnswer": {"@type": "Answer", "text": "You can get instant cash for your phone at 2A Electronics Service, located at 4859 W Irving Park Rd, Chicago, IL 60641. We offer competitive payouts on the spot for iPhones, iPads, MacBooks, and gaming consoles in good working condition."}},
            {"@type": "Question", "name": "Do you buy broken or financed iPhones?", "acceptedAnswer": {"@type": "Answer", "text": "Yes, 2A Electronics buys brand new, used, finance-locked, and cracked or broken devices. Bring your device to our Chicago store for a free 5-minute appraisal and immediate cash payout."}},
            {"@type": "Question", "name": "How long does an electronics repair take at 2A Electronics?", "acceptedAnswer": {"@type": "Answer", "text": "Most cell phone screen and battery replacements are completed same-day within 30 to 45 minutes. Game console HDMI repairs and laptop service typically take 1-2 business days depending on diagnostics."}}
          ]
        })}</script>
      </Helmet>

      <style>{`
        html, body, #root { overflow-x: clip; background: #F7F7F5; }
        .tw-home { --pearl:#F7F7F5; --ink:#0A0A0A; --blue:#0047FF; --green:#059669; --green-vivid:#00B25A;
          font-family:'Satoshi','Outfit',system-ui,sans-serif; color:var(--ink); background:var(--pearl);
          -webkit-font-smoothing:antialiased; letter-spacing:-0.005em; }
        .tw-home ::selection { background:#0047FF; color:#fff; }
        .disp { font-family:'Cabinet Grotesk','Outfit',sans-serif; font-weight:900; letter-spacing:-0.045em; line-height:0.94; }
        .over { font-family:'Satoshi',sans-serif; font-weight:700; text-transform:uppercase; letter-spacing:0.22em; font-size:0.7rem; }
        .tw-home a:focus-visible, .tw-home button:focus-visible { outline:2px solid #0047FF; outline-offset:3px; border-radius:6px; }

        /* Signature motif: the Out-of-Bounds Arch */
        .arch { border-radius:4.5rem 0.9rem 4.5rem 0.9rem; overflow:hidden; position:relative; }
        .arch-sm { border-radius:2.4rem 0.6rem 2.4rem 0.6rem; overflow:hidden; position:relative; }

        .btn { display:inline-flex; align-items:center; justify-content:center; gap:0.55rem; border-radius:999px;
          font-family:'Satoshi',sans-serif; font-weight:700; font-size:0.95rem; padding:0.95rem 1.9rem; cursor:pointer;
          transition:transform .22s cubic-bezier(.2,.8,.2,1), box-shadow .22s ease, background-color .2s ease, border-color .2s ease; }
        .btn:hover { transform:translateY(-2px); }
        .btn-blue { background:var(--blue); color:#fff; box-shadow:0 16px 34px -14px rgba(0,71,255,.65); }
        .btn-blue:hover { background:#0038cc; }
        .btn-green { background:var(--green); color:#fff; box-shadow:0 16px 34px -14px rgba(5,150,105,.6); }
        .btn-green:hover { background:#047857; }
        .btn-dark { background:var(--ink); color:#fff; }
        .btn-dark:hover { background:#222; }
        .btn-light { background:#fff; color:var(--ink); box-shadow:0 10px 26px -16px rgba(10,10,10,.4); }
        .btn-ghost { background:transparent; color:var(--ink); border:1.5px solid rgba(10,10,10,.16); }
        .btn-ghost:hover { border-color:var(--ink); background:rgba(10,10,10,.03); }
        .btn-ghost-inv { background:transparent; color:#fff; border:1.5px solid rgba(255,255,255,.28); }
        .btn-ghost-inv:hover { border-color:#fff; background:rgba(255,255,255,.08); }

        [data-reveal] { opacity:0; transform:translateY(30px); transition:opacity .75s cubic-bezier(.2,.8,.2,1), transform .75s cubic-bezier(.2,.8,.2,1); }
        [data-reveal].in { opacity:1; transform:none; }
        [data-reveal][data-d="1"]{ transition-delay:.09s } [data-reveal][data-d="2"]{ transition-delay:.18s }
        [data-reveal][data-d="3"]{ transition-delay:.27s } [data-reveal][data-d="4"]{ transition-delay:.36s }
        @keyframes floaty { 0%,100%{ transform:translateY(0) } 50%{ transform:translateY(-14px) } }
        .floaty { animation:floaty 7s ease-in-out infinite; }
        .no-scrollbar::-webkit-scrollbar{ display:none } .no-scrollbar{ -ms-overflow-style:none; scrollbar-width:none }
        @media (prefers-reduced-motion: reduce){ [data-reveal]{opacity:1;transform:none;transition:none} .floaty{animation:none} }
        @media (max-width:640px){ .tw-home h1{ font-size:2.9rem!important } }
      `}</style>

      {/* ===================== 1 · HERO ===================== */}
      <section className="relative pt-[64px] sm:pt-[104px] overflow-hidden lg:min-h-screen lg:flex lg:flex-col">
        {/* ambient color wash */}
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div className="absolute -top-24 right-[8%] w-[38rem] h-[38rem] rounded-full blur-3xl opacity-50" style={{ background:'radial-gradient(circle,#0047FF22,transparent 62%)' }} />
          <div className="absolute top-40 -left-24 w-[34rem] h-[34rem] rounded-full blur-3xl opacity-40" style={{ background:'radial-gradient(circle,#00B25A22,transparent 62%)' }} />
        </div>

        <div className="relative w-full lg:flex-1 max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 pt-10 sm:pt-16 pb-6 grid lg:grid-cols-12 gap-8 lg:gap-10 items-center">
          <div className="lg:col-span-5 xl:col-span-5">
            <div className="over text-slate-500 flex items-center gap-2">
              <span className="inline-block w-8 h-px bg-slate-400" /> 2A Electronics · Chicago
            </div>
            <h1 className="disp mt-5 text-[3.1rem] sm:text-7xl xl:text-[5.6rem]">
              Your tech.<br />
              <span style={{ color:'var(--blue)' }}>Fixed.</span>{' '}
              <span style={{ color:'var(--green-vivid)' }}>Sold.</span>{' '}
              <span>Upgraded.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-md font-medium">
              Chicago's home for same-day repairs, instant cash for your old devices,
              and certified refurbished tech that costs a whole lot less.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3 max-w-md">
              <div className="flex flex-col min-w-0 sm:flex-1">
                <button onClick={() => navigate('/repairs')} data-testid="repair-device-btn" className="btn btn-blue w-full">
                  <Wrench className="w-4 h-4 shrink-0" /> Repair my device
                </button>
                <HomeContactChoice kind="repair" />
              </div>
              <div className="flex flex-col min-w-0 sm:flex-1">
                <button onClick={() => navigate('/sell')} data-testid="sell-device-btn" className="btn btn-green w-full">
                  <DollarSign className="w-4 h-4 shrink-0" /> Sell my device
                </button>
                <HomeContactChoice kind="sell" />
              </div>
            </div>

            {/* three-path chips */}
            <div className="mt-8 flex items-center gap-5 text-sm font-bold">
              <button onClick={() => scrollTo('world-repair')} className="group inline-flex items-center gap-1.5 text-slate-700 hover:text-[color:var(--blue)] transition-colors" data-testid="path-repair">
                Repair <ArrowRight className="w-3.5 h-3.5 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
              </button>
              <span className="w-px h-4 bg-slate-300" />
              <button onClick={() => scrollTo('world-sell')} className="group inline-flex items-center gap-1.5 text-slate-700 hover:text-[color:var(--green)] transition-colors" data-testid="path-sell">
                Sell <ArrowRight className="w-3.5 h-3.5 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
              </button>
              <span className="w-px h-4 bg-slate-300" />
              <button onClick={() => scrollTo('world-shop')} className="group inline-flex items-center gap-1.5 text-slate-700 hover:text-black transition-colors" data-testid="path-shop">
                Shop <ArrowRight className="w-3.5 h-3.5 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
              </button>
            </div>

            <div className="mt-9 flex flex-wrap items-center gap-x-6 gap-y-2 text-[13px] font-semibold text-slate-500">
              <Link to="/reviews" data-testid="hero-reviews-link" className="inline-flex items-center gap-1.5 hover:text-slate-900 transition-colors underline-offset-4 hover:underline"><Star className="w-4 h-4 fill-amber-400 text-amber-400" /> 5.0 · 470 reviews</Link>
              <Link to="/apple-authorized" data-testid="hero-irp-link" className="inline-flex items-center gap-1.5 hover:text-slate-900 transition-colors underline-offset-4 hover:underline"><BadgeCheck className="w-4 h-4 text-[color:var(--blue)]" /> Apple IRP Certified</Link>
              <Link to="/repairs" data-testid="hero-sameday-link" className="inline-flex items-center gap-1.5 hover:text-slate-900 transition-colors underline-offset-4 hover:underline"><Clock className="w-4 h-4 text-[color:var(--green)]" /> Same-day service</Link>
            </div>
          </div>

          {/* Out-of-Bounds Arch hero visual - real store photos */}
          <div className="lg:col-span-7 xl:col-span-7 relative mt-4 lg:mt-0">
            <div className="flex items-stretch gap-3 sm:gap-4 w-full max-w-[720px] mx-auto lg:mr-0 lg:ml-auto lg:h-[70vh] lg:max-h-[720px]">
              {/* primary: storefront (left, defines height) */}
              <div className="arch relative w-[56%] sm:w-[58%] aspect-[4/5] lg:aspect-auto lg:h-full shadow-[0_44px_90px_-42px_rgba(10,10,10,0.5)]">
                <img src={IMG.storefront} alt="2A Electronics storefront at 4859 W Irving Park Rd, Chicago"
                  className="w-full h-full object-cover" loading="eager" fetchPriority="high" decoding="async" />
                <div aria-hidden className="absolute inset-0" style={{ background:'linear-gradient(180deg,transparent 66%,rgba(10,10,10,0.25))' }} />
              </div>
              {/* right column: interior photo on top, tagline + location button below */}
              <div className="w-[44%] sm:w-[42%] flex flex-col gap-3 sm:gap-4">
                <div className="arch-sm relative flex-1 min-h-0 ring-[5px] ring-[#F7F7F5] shadow-[0_34px_70px_-30px_rgba(10,10,10,0.6)]">
                  <img src={IMG.interior} alt="Inside the 2A Electronics store with accessories wall and service counter"
                    className="w-full h-full object-cover" loading="lazy" decoding="async" />
                </div>
                <div className="bg-white rounded-2xl shadow-[0_24px_60px_-24px_rgba(10,10,10,0.5)] p-4 sm:p-5">
                  <div className="disp text-lg sm:text-2xl leading-[0.95]">Real store.<br />Real deals.</div>
                  <div className="mt-1.5 flex items-center gap-1.5 text-[11px] font-semibold text-slate-500">
                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${isOpen ? 'bg-emerald-500 animate-pulse' : 'bg-slate-400'}`} /> {storeStatus}
                  </div>
                  <Link to="/location" data-testid="hero-location-btn" className="btn btn-dark mt-3 w-full !py-2.5 !px-4 text-sm">
                    <MapPin className="w-4 h-4" /> Location
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Live promo ticker along the bottom edge */}
        <div className="relative mt-4"><TickerBanner /></div>
      </section>

      {/* ===================== 2 · THREE WORLDS ===================== */}
      {/* --- REPAIR --- */}
      <section id="world-repair" className="scroll-mt-24 relative overflow-hidden" style={{ background:'#EEF3FF' }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-28 grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          <div data-reveal>
            <div className="over" style={{ color:'var(--blue)' }}>Repair</div>
            <h2 className="disp mt-4 text-4xl sm:text-6xl">Something broke?<br /><span style={{ color:'var(--blue)' }}>Let's fix it.</span></h2>
            <p className="mt-5 text-lg text-slate-600 max-w-md font-medium">
              Cracked screens, dying batteries. Our Chicago
              bench fixes phones, laptops, tablets and game systems, most in the same day.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/repairs" data-testid="repair-world-cta" className="btn btn-blue">Start a repair <ArrowRight className="w-4 h-4" /></Link>
              <a href="tel:7737665814" data-testid="repair-call-btn" className="btn btn-ghost"><Phone className="w-4 h-4" /> (773) 766-5814</a>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3 text-sm font-bold text-slate-600">
              <span className="inline-flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[color:var(--blue)]" /> 1-month warranty</span>
              <span className="inline-flex items-center gap-2"><Sparkles className="w-4 h-4 text-[color:var(--blue)]" /> Free diagnostics</span>
              <span className="inline-flex items-center gap-2"><Wrench className="w-4 h-4 text-[color:var(--blue)]" /> Micro-soldering</span>
            </div>
            <Link to="/referral" data-testid="repair-referral-btn" className="mt-6 inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-300 px-5 py-2.5 text-sm font-bold text-emerald-700 hover:bg-emerald-100 hover:border-emerald-400 transition-colors shadow-sm">
              <Gift className="w-4 h-4" /> Refer a Friend — Get $10 Cash Bonus <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="relative" data-reveal data-d="2">
            <div className="arch aspect-[5/4] sm:aspect-[6/5] shadow-[0_40px_90px_-40px_rgba(0,71,255,0.4)]">
              <img src={IMG.repairBench} alt="Professional phone repair at 2A Electronics Chicago: technician repairing a smartphone on a repair bench" className="w-full h-full object-cover" loading="lazy" decoding="async" />
              <div className="absolute inset-0" style={{ background:'linear-gradient(180deg,transparent 55%,rgba(0,20,80,0.35))' }} />
            </div>
            <div className="absolute -bottom-5 -left-3 sm:-left-6 rounded-2xl bg-white px-5 py-4 shadow-xl">
              <div className="disp text-3xl" style={{ color:'var(--blue)' }}>14 Years</div>
              <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Of experience</div>
            </div>
          </div>
        </div>
      </section>

      {/* --- SELL --- */}
      <section id="world-sell" className="scroll-mt-24 relative overflow-hidden" style={{ background:'#EAFBF1' }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-28 grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          <div className="relative order-2 lg:order-1" data-reveal data-d="2">
            <div className="arch aspect-[5/4] sm:aspect-[6/5] flex items-center justify-center"
              style={{ background:'linear-gradient(150deg,#d9f7e6 0%,#eafbf1 100%)' }}>
              <div aria-hidden className="absolute inset-0 opacity-40" style={{ backgroundImage:'radial-gradient(rgba(5,150,105,0.12) 1px, transparent 1px)', backgroundSize:'20px 20px' }} />
              <img src={IMG.fanBuyback} alt="We buy iPhones, MacBooks and iPads for cash in Chicago" className="floaty relative w-[86%] max-w-[460px] object-contain drop-shadow-[0_28px_36px_rgba(5,150,105,0.28)]" loading="lazy" decoding="async" />
            </div>
            <div className="absolute -bottom-5 -right-3 sm:-right-6 rounded-2xl bg-white px-5 py-4 shadow-xl">
              <div className="disp text-3xl" style={{ color:'var(--green-vivid)' }}>5 min</div>
              <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Appraisal & payout</div>
            </div>
          </div>
          <div className="order-1 lg:order-2" data-reveal>
            <div className="over" style={{ color:'var(--green)' }}>Sell</div>
            <h2 className="disp mt-4 text-4xl sm:text-6xl">Done with it?<br /><span style={{ color:'var(--green-vivid)' }}>Turn it into cash.</span></h2>
            <p className="mt-5 text-lg text-slate-600 max-w-md font-medium">
              New, used, financed. We take iPhones, Samsung, MacBooks, iPads and consoles.
              Get a fair quote in minutes and walk out with cash the same day.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/sell" data-testid="buyback-sell-btn" className="btn btn-green">Get my quote <ArrowRight className="w-4 h-4" /></Link>
              <a href="tel:7737665814" data-testid="buyback-call-btn" className="btn btn-ghost"><Phone className="w-4 h-4" /> (773) 766-5814</a>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              {[['1','Get a quote'],['2','Bring it in or ship free'],['3','Get paid today']].map(([n,t]) => (
                <span key={n} className="inline-flex items-center gap-2 text-sm font-bold text-slate-700">
                  <span className="w-6 h-6 rounded-full bg-[color:var(--green)] text-white flex items-center justify-center text-xs">{n}</span>{t}
                </span>
              ))}
            </div>
            <Link to="/referral" data-testid="sell-referral-btn" className="mt-6 inline-flex items-center gap-2 rounded-full bg-white border border-emerald-300 px-5 py-2.5 text-sm font-bold text-emerald-700 hover:bg-emerald-50 hover:border-emerald-400 transition-colors shadow-sm">
              <Gift className="w-4 h-4" /> Refer a Friend — Get $10 Cash Bonus <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* --- SHOP (obsidian) --- */}
      <section id="world-shop" data-testid="home-refurbished-section" className="scroll-mt-24 relative overflow-hidden" style={{ background:'#0A0A0A', color:'#fff' }}>
        <div aria-hidden className="absolute inset-0 opacity-30" style={{ backgroundImage:'radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1px)', backgroundSize:'26px 26px' }} />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-28">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6" data-reveal>
            <div>
              <div className="over text-emerald-400">Certified Refurbished</div>
              <h2 className="disp mt-4 text-4xl sm:text-6xl text-white">Better tech.<br /><span className="text-white/55">Better value.</span></h2>
              <p className="mt-5 text-lg text-white/60 max-w-lg font-medium">
                Every device is inspected, graded and warranted by our own Chicago technicians.
                Premium hardware, a fraction of retail.
              </p>
            </div>
            <div className="flex gap-3">
              <Link to="/refurbished" data-testid="home-refurb-browse-all" className="btn btn-light">Shop the store <ArrowRight className="w-4 h-4" /></Link>
              <a href="tel:7737665814" data-testid="home-refurb-call" className="btn btn-ghost-inv"><Phone className="w-4 h-4" /> Reserve</a>
            </div>
          </div>

          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6" data-reveal data-d="1">
            {(shopDevices.length ? shopDevices : Array.from({ length: 4 })).slice(0, 8).map((d, i) => (
              d ? (
                <Link key={d.id} to={listingUrl(d)} data-testid={`home-refurb-device-${d.id}`}
                  className="group block">
                  <div className="arch-sm aspect-square bg-white/[0.04] border border-white/10 overflow-hidden transition-colors group-hover:border-emerald-400/60">
                    {d.images?.[0] ? (
                      <img src={d.images[0]} alt={d.device_name || `${d.brand} ${d.model}`} loading={i < 2 ? 'eager' : 'lazy'} decoding="async" className="w-full h-full object-contain p-5 transition-transform duration-500 group-hover:scale-105" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white/20"><Package className="w-10 h-10" /></div>
                    )}
                    <span className={`absolute top-3 left-3 text-[10px] font-bold px-2 py-0.5 rounded-md ${GRADE_BG[d.condition_grade] || GRADE_BG.B}`}>Grade {d.condition_grade || 'B'}</span>
                    {d.savings_percent > 0 && <span className="absolute top-3 right-3 bg-white text-black text-[10px] font-bold px-2 py-0.5 rounded-md">-{d.savings_percent}%</span>}
                  </div>
                  <div className="mt-3 px-1">
                    <h3 className="text-sm font-bold text-white line-clamp-1 group-hover:text-emerald-300 transition-colors">{d.device_name || `${d.brand} ${d.model}`}</h3>
                    <div className="mt-1 flex items-baseline gap-2">
                      <span className="disp text-xl text-white">${Number(d.price).toFixed(0)}</span>
                      {d.original_price > d.price && <span className="text-xs text-white/40 line-through">${Number(d.original_price).toFixed(0)}</span>}
                    </div>
                  </div>
                </Link>
              ) : (
                <div key={i} className="arch-sm aspect-square bg-white/[0.04] border border-white/10 animate-pulse" />
              )
            ))}
          </div>
        </div>
        <div className="relative"><RecentlyViewedStrip variant="dark" /></div>
      </section>

      {/* ===================== 6 · TRUST + REVIEWS ===================== */}
      <section className="relative overflow-hidden" style={{ background:'#0A0A0A', color:'#fff' }}>
        <div aria-hidden className="absolute inset-0 opacity-25" style={{ backgroundImage:'radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1px)', backgroundSize:'26px 26px' }} />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
          <div className="max-w-3xl" data-reveal>
            <div className="over text-emerald-400">Trusted by Chicago</div>
            <h2 className="disp mt-4 text-4xl sm:text-7xl text-white">
              Loved by the<br />neighborhood. <span className="inline-flex items-center gap-2 align-middle text-amber-400 text-3xl sm:text-5xl">★★★★★</span>
            </h2>
            <p className="mt-5 text-lg text-white/60 font-medium">
              A perfect 5.0 across 470+ reviews, and an Apple Independent Repair Provider.
              Real people, real devices, real results.
            </p>
          </div>

          <div className="mt-12 flex gap-5 overflow-x-auto no-scrollbar snap-x -mx-5 px-5 sm:mx-0 sm:px-0" data-reveal data-d="1">
            {displayReviews.map((r, i) => (
              <figure key={r.id || i} data-testid={`review-card-${i}`} className="snap-start flex-shrink-0 w-[300px] sm:w-[360px] rounded-3xl bg-white/[0.05] border border-white/10 p-6">
                <div className="flex gap-0.5 mb-4">{[...Array(r.rating || 5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-amber-400 text-amber-400" />)}</div>
                <blockquote className="text-white/85 leading-relaxed text-[15px]">&ldquo;{r.text}&rdquo;</blockquote>
                <figcaption className="mt-5 text-sm font-bold text-white/70">
                  {r.author_name || r.name} <span className="text-white/35 font-medium">· {r.device} · {r.platform === 'yelp' ? 'Yelp' : 'Google'}</span>
                </figcaption>
              </figure>
            ))}
          </div>
          <div className="mt-8" data-reveal>
            <a href="https://share.google/jb95xsqhjI6kHM1bj" target="_blank" rel="noopener noreferrer" data-testid="read-all-reviews-btn" className="btn btn-ghost-inv">Read all reviews <ExternalLink className="w-4 h-4" /></a>
          </div>
        </div>
      </section>

      {/* ===================== 7 · ALREADY A CUSTOMER (tracking/portal) ===================== */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-20">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8" data-reveal>
            <div>
              <div className="over text-slate-500">Already a customer?</div>
              <h2 className="disp mt-3 text-3xl sm:text-5xl">Track it. Manage it.</h2>
            </div>
            <Link to="/customer/login" data-testid="customer-login-btn" className="btn btn-ghost self-start">Log in to your portal <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 rounded-3xl overflow-hidden border border-slate-200" data-reveal data-d="1">
            {[
              { t:'Track Repairs', s:'Live status on your device', to:'/track-repair', testid:'track-website-repairs-card', Icon:Wrench },
              { t:'Track Sales', s:'Follow your sale & payout', to:'/track-sale', testid:'track-website-sales-card', Icon:DollarSign },
              { t:'Protection Plan', s:'6-month same-issue cover', to:'/repair-protection-plan', testid:'protection-plan-card-home', Icon:ShieldCheck },
              { t:'Customer Portal', s:'Orders & history in one place', to:'/customer/login', testid:'customer-portal-card', Icon:UserCircle },
            ].map((b) => (
              <Link key={b.t} to={b.to} data-testid={b.testid} className="group bg-white hover:bg-slate-50 transition-colors p-6 flex flex-col gap-6 min-h-[150px]">
                <b.Icon className="w-6 h-6 text-slate-900 group-hover:text-[color:var(--blue)] transition-colors" strokeWidth={1.5} />
                <div className="mt-auto">
                  <div className="font-bold text-base flex items-center gap-1">{b.t} <ArrowUpRight className="w-4 h-4 text-slate-300 group-hover:text-[color:var(--blue)] transition-colors" /></div>
                  <div className="text-sm text-slate-500 mt-1">{b.s}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== 8 · CHICAGO LOCATION ===================== */}
      <section className="relative overflow-hidden">
        <div className="relative h-[360px] sm:h-[440px]">
          <img src={IMG.chicago} alt="2A Electronics, proudly local to Chicago" className="absolute inset-0 w-full h-full object-cover" loading="lazy" decoding="async" />
          <div className="absolute inset-0" style={{ background:'linear-gradient(90deg,rgba(10,10,10,0.55),rgba(10,10,10,0.15))' }} />
          <div className="relative max-w-7xl mx-auto px-5 sm:px-8 h-full flex items-center">
            <div className="text-white max-w-md" data-reveal>
              <div className="over text-emerald-300">Local to Chicago</div>
              <h2 className="disp mt-4 text-4xl sm:text-6xl">Come say hi.</h2>
              <p className="mt-4 text-white/80 font-medium">Minutes from Irving Park, Logan Square & Avondale. Free walk-in diagnostics and 30-minute screen swaps.</p>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-5 sm:px-8 -mt-16 sm:-mt-20 relative pb-20">
          <div className="arch-sm bg-white shadow-[0_40px_90px_-40px_rgba(10,10,10,0.5)] p-6 sm:p-10 grid md:grid-cols-3 gap-8" data-reveal>
            <div>
              <div className="over text-slate-400">Address</div>
              <div className="disp text-2xl mt-2">4859 W Irving<br />Park Rd</div>
              <div className="text-slate-500 font-medium mt-1">Chicago, IL 60641</div>
              <span className={`mt-3 inline-flex items-center gap-2 text-sm font-bold ${isOpen ? 'text-emerald-600' : 'text-slate-500'}`}>
                <span className={`w-2 h-2 rounded-full ${isOpen ? 'bg-emerald-500' : 'bg-slate-400'}`} /> {isOpen ? 'Open now' : 'Closed now'}
              </span>
            </div>
            <div>
              <div className="over text-slate-400">Hours</div>
              <div className="mt-3 space-y-2 text-sm">
                {[['Mon-Fri','10:00-18:00'],['Saturday','10:00-16:00'],['Sunday','Closed']].map(([d,h]) => (
                  <div key={d} className="flex items-center justify-between border-b border-slate-100 pb-2">
                    <span className="font-semibold text-slate-600">{d}</span><span className="font-bold text-slate-900">{h}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex flex-col gap-3">
              <div className="over text-slate-400">Get in touch</div>
              <a href="https://www.google.com/maps/dir/?api=1&destination=4859+W+Irving+Park+Rd,+Chicago,+IL+60641" target="_blank" rel="noopener noreferrer" data-testid="store-location-icon" className="btn btn-blue"><Navigation className="w-4 h-4" /> Directions</a>
              <a href="tel:7737665814" data-testid="store-phone-icon" className="btn btn-ghost"><Phone className="w-4 h-4" /> (773) 766-5814</a>
              <div className="flex gap-3">
                <Link to="/contact" data-testid="contact-sales-btn" className="btn btn-ghost flex-1">Contact</Link>
                <a href="https://share.google/vwOUS3xIFPC8lkNXL" target="_blank" rel="noopener noreferrer" data-testid="google-reviews-card" className="btn btn-ghost flex-1">Reviews <ExternalLink className="w-3.5 h-3.5" /></a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== 9 · FAQ ===================== */}
      <section id="faq-section" className="relative overflow-hidden" style={{ background:'#F1F1EE' }}>
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
          <div className="flex items-end justify-between gap-4" data-reveal>
            <div>
              <div className="over text-slate-500">Good to know</div>
              <h2 className="disp mt-3 text-4xl sm:text-6xl">Got questions?</h2>
            </div>
            <button onClick={() => setFaqExpanded((v) => !v)} data-testid="faq-toggle-btn" aria-expanded={faqExpanded} className="btn btn-ghost">
              {faqExpanded ? 'Hide' : 'Show'} <ChevronDown className={`w-4 h-4 transition-transform ${faqExpanded ? 'rotate-180' : ''}`} />
            </button>
          </div>
          {faqExpanded && (
            <div className="mt-8">
              {faqs.map((f, i) => (
                <div key={i} data-testid={`faq-question-${i}`} onClick={() => setActiveQuestion(activeQuestion === i ? null : i)} className="border-b border-slate-200 py-5 cursor-pointer group">
                  <div className="flex items-center justify-between gap-4">
                    <h3 className="text-lg font-bold group-hover:text-[color:var(--blue)] transition-colors">{f.q}</h3>
                    {activeQuestion === i ? <Minus className="w-5 h-5 flex-shrink-0 text-[color:var(--blue)]" /> : <Plus className="w-5 h-5 flex-shrink-0 text-slate-400 group-hover:text-slate-900 transition-colors" />}
                  </div>
                  <p className={`text-slate-600 leading-relaxed transition-all duration-300 ${activeQuestion === i ? 'opacity-100 max-h-[400px] mt-3' : 'opacity-0 max-h-0 overflow-hidden'}`}>{f.a}</p>
                </div>
              ))}
              <div className="mt-7"><Link to="/faq" data-testid="access-faq-btn" className="btn btn-ghost">View all FAQs <ArrowRight className="w-4 h-4" /></Link></div>
            </div>
          )}
        </div>
      </section>

      {/* ===================== 10 · FINAL CTA ===================== */}
    </div>
  );
};

export default HomeNew;
